import { useAuth } from '@agentnet/auth';
import { Badge as MuiBadge } from '@material-ui/core';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { CheckCircleOutlineOutlined, ErrorOutlineOutlined, ReportProblemOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useOrderInfo } from 'hooks/OrderInfoContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FileDataContext } from '../../hooks/FileDataContext';
import { ProfileContext } from '../../hooks/ProfileContext';
import { OrderManagementLinks } from './LeftNavLinks';
import { NavLinkBadgeStatus, NavLinkType } from './NavLinkType';

type NavBadgeProps = {
  link: NavLinkType;
  shouldShowDot?: boolean;
};

const StyledBadge = withStyles((theme) => ({
  root: {
    '&.warning span.MuiBadge-badge': {
      backgroundColor: theme.palette.warning.main,
    },
    '&.idle span.MuiBadge-badge': {
      display: 'none',
    },
  },
  badge: {
    borderRadius: '50%',
    height: '26px',
    width: '26px',
    padding: '0 6px',
    backgroundColor: theme.palette.primary.main,
    color: `${theme.palette.common.white} !important`,
    fontSize: '12px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&[data-variant="dot"]': {
      height: '10px',
      minWidth: '10px',
      padding: '0',
    },
  },
  dot: {
    width: 8,
    height: 8,
    backgroundColor: theme.palette.success.main,
    padding: 0,
    transform: `scale(1) translate(-150%, -150%)`,
  },
}))(MuiBadge);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    badge: { marginLeft: 'auto' },
    checkCircle: { '& path': { fill: theme.palette.success.main } },
    warningOutlined: { '& path': { fill: theme.palette.warning.main } },
    errorOutlined: { '& path': { fill: theme.palette.error.main } },
  }),
);

const NavBadge = ({ link, shouldShowDot = false }: NavBadgeProps): JSX.Element => {
  const { notification: variant = 'standard', notificationStatus: color, badgeContentFunction } = link;
  const { userFirm, badgeRefreshKey } = useContext(ProfileContext) ?? {};
  const fileDataContext = useContext(FileDataContext);
  const fileData = fileDataContext?.fileData;
  const { fileId, serviceStatus, fileStatus } = fileData || {};
  const firmId = userFirm?.firmId ?? '';
  const { getAccessToken } = useAuth();

  const [badgeContent, setBadgeContent] = useState<NavLinkBadgeStatus | number>();
  const sessionFileId = fileDataContext?.sessionFileId ?? null;
  const classes = useStyles();
  const { selectedFilesAmount } = useOrderInfo();
  const fincenReportContext = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const location = useLocation();
  const isFincenLink =
    link?.testId?.includes('FinCEN') ||
    link?.testId?.includes('Fincen') ||
    link?.path?.includes('fincen-reporting') ||
    link?.linkText?.toLowerCase().includes('fincen');

  // Track which file ID the current badge content belongs to
  const [badgeFileId, setBadgeFileId] = useState<string | null>(null);
  const contextFileId = fincenReportContext?.currentFileId ?? null;
  const hasValidationResults = fincenReportContext?.hasValidationResults ?? false;

  // Function to get page-specific FinCEN validation badge status
  const getFincenValidationStatus = useCallback((): NavLinkBadgeStatus | undefined => {
    const isMainFincenLink =
      link?.testId === 'GlobalNavFilesFincenReporting' || link?.linkText === 'FinCEN Real Estate Report';

    if (isMainFincenLink) {
      return serviceStatus?.hasCompletedFincen === true ? NavLinkBadgeStatus.Success : NavLinkBadgeStatus.Idle;
    }

    // For sub-pages, we need fincenReportContext
    if (!fincenReportContext) return NavLinkBadgeStatus.Idle;

    const {
      fincenReportStatus,
      fincenReport,
      reportingPartyValidationErrors,
      paymentValidationErrors,
      propertyAddressValidationErrors,
      transferorValidationErrors,
      transfereeValidationErrors,
      generateButtonClicked,
      certifierValidationErrors,
    } = fincenReportContext;

    // For sub-pages, wait for validation results
    if (fincenReportStatus !== 'success' || !hasValidationResults) {
      return undefined;
    }

    const reportIsComplete = serviceStatus?.hasCompletedFincen === true;

    if (reportIsComplete) {
      return NavLinkBadgeStatus.Success;
    }

    // Red badges only show when Generate Report was clicked in current session
    const shouldShowRedBadges = generateButtonClicked;

    // Helper function to determine page-specific errors
    const getPageErrors = (): number => {
      switch (link?.testId) {
        case 'fincen-nav-report-party':
          return reportingPartyValidationErrors.length;
        case 'fincen-nav-payment-info':
          return paymentValidationErrors.length;
        case 'fincen-nav-property-info':
          return propertyAddressValidationErrors.length;
        case 'fincen-nav-transferor-info':
          return transferorValidationErrors.length;
        case 'fincen-nav-transferee-info':
          return transfereeValidationErrors.length;
        case 'fincen-nav-filing-information':
        case 'fincen-nav-summary':
          return 0; // These pages always show success
        case 'fincen-nav-certify-generate':
          // Certify page only checks certifier validation errors (checkbox and certifier fields)
          return certifierValidationErrors.length;
        default: {
          // For the main "Fincen Real Estate Report" link, check all errors
          return (
            reportingPartyValidationErrors.length +
            paymentValidationErrors.length +
            propertyAddressValidationErrors.length +
            transferorValidationErrors.length +
            transfereeValidationErrors.length
          );
        }
      }
    };

    const pageErrors = getPageErrors();

    // Special handling for Certify & Generate Report badge
    if (link?.testId === 'fincen-nav-certify-generate') {
      if (pageErrors == 0) {
        return NavLinkBadgeStatus.Success;
      } else if (pageErrors > 0 && shouldShowRedBadges) {
        return NavLinkBadgeStatus.Error;
      } else {
        return NavLinkBadgeStatus.Idle;
      }
    }

    // For all other pages
    if (pageErrors > 0) {
      // Has errors - only show red if Generate Report was clicked
      return shouldShowRedBadges ? NavLinkBadgeStatus.Error : NavLinkBadgeStatus.Idle;
    } else {
      // No errors - show green
      return NavLinkBadgeStatus.Success;
    }
  }, [
    link?.testId,
    link?.linkText,
    serviceStatus?.hasCompletedFincen,
    badgeContent,
    fincenReportContext?.fincenReportStatus,
    fincenReportContext?.hasValidationResults,
    fincenReportContext?.fincenReport?.reportStatusCdId,
    fincenReportContext?.currentFileId,
    fincenReportContext?.reportingPartyValidationErrors,
    fincenReportContext?.paymentValidationErrors,
    fincenReportContext?.propertyAddressValidationErrors,
    fincenReportContext?.transferorValidationErrors,
    fincenReportContext?.transfereeValidationErrors,
    fincenReportContext?.certifierValidationErrors,
    fincenReportContext?.generateButtonClicked,
  ]);

  useEffect(() => {
    let isCancelled = false;
    const fetchBadgeContent = async () => {
      // Invoice Summary is a transient payment page — nav badge counts don't change there
      if (location.pathname.includes('/invoice-summary')) return;
      if (badgeContentFunction && userFirm?.firmId) {
        try {
          const currentSessionFileId = sessionStorage.getItem('fileId');
          const accessToken = await getAccessToken();
          const content = await badgeContentFunction(
            accessToken,
            { fileId, firmId },
            currentSessionFileId && currentSessionFileId === fileId ? fileDataContext?.setFileData : undefined,
          );
          if (!isCancelled) {
            setBadgeContent(content);
          }
        } catch (err) {
          console.error(`[NavBadge] Failed to fetch badge for "${link.linkText}":`, err);
        }
      }
    };
    fetchBadgeContent();
    return () => {
      isCancelled = true;
    };
  }, [
    badgeContentFunction,
    userFirm?.firmId,
    sessionFileId,
    fileId,
    serviceStatus?.hasRemittableCpl,
    serviceStatus?.hasActiveJacket,
    serviceStatus?.hasStandAloneEndorsement,
    serviceStatus?.hasOpenEndorsement,
    serviceStatus?.isPricingValid,
    serviceStatus?.isRemitValid,
    fileStatus,
    location.pathname,
    badgeRefreshKey,
  ]);

  useEffect(() => {
    if (location.pathname === '/order-management') {
      const fetchOrderManagementBadgeContent = async () => {
        const orderManagementLink = OrderManagementLinks.find((link) => link.testId === 'OrderManagementDashboard');
        if (orderManagementLink?.badgeContentFunction) {
          const accessToken = await getAccessToken();
          const result = await orderManagementLink.badgeContentFunction(accessToken, { selectedFilesAmount });
          setBadgeContent(result);
        }
      };
      fetchOrderManagementBadgeContent();
    }
  }, [selectedFilesAmount]);

  useEffect(() => {
    const isMainFincenLink =
      link?.testId === 'GlobalNavFilesFincenReporting' || link?.linkText === 'FinCEN Real Estate Report';

    if (isFincenLink && isMainFincenLink) {
      // For main FinCEN link, only check serviceStatus - no need for fincenReportContext
      const fincenStatus = getFincenValidationStatus();
      if (fincenStatus !== undefined) {
        setBadgeContent(fincenStatus);
      }
    } else if (isFincenLink && fincenReportContext) {
      // For sub-pages, use fincenReportContext
      const fincenStatus = getFincenValidationStatus();
      if (fincenStatus !== undefined) {
        const timeoutId = setTimeout(() => {
          setBadgeContent(fincenStatus);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    } else if (isFincenLink && !fincenReportContext) {
      setBadgeContent(NavLinkBadgeStatus.Idle);
    }
  }, [
    link?.testId,
    link?.path,
    link?.linkText,
    location.pathname,
    fincenReportContext,
    getFincenValidationStatus,
    isFincenLink,
    contextFileId,
    serviceStatus?.hasCompletedFincen,
    // Add validation error arrays as dependencies to ensure re-evaluation when they change
    fincenReportContext?.reportingPartyValidationErrors,
    fincenReportContext?.paymentValidationErrors,
    fincenReportContext?.propertyAddressValidationErrors,
    fincenReportContext?.transferorValidationErrors,
    fincenReportContext?.transfereeValidationErrors,
    fincenReportContext?.certifierValidationErrors,
    fincenReportContext?.generateButtonClicked,
    fincenReportContext?.autoValidationTriggered,
    fincenReportContext?.hasValidationResults,
    fincenReportContext?.fincenReportStatus,
  ]);

  // Update badgeFileId when badge content changes for FinCEN links
  useEffect(() => {
    if (isFincenLink && contextFileId && badgeContent !== NavLinkBadgeStatus.Idle && badgeContent !== undefined) {
      setBadgeFileId(contextFileId);
    }
  }, [isFincenLink, contextFileId, badgeContent]);

  // Reset badge when switching to a different file
  useEffect(() => {
    if (isFincenLink && contextFileId && badgeFileId && contextFileId !== badgeFileId) {
      setBadgeContent(NavLinkBadgeStatus.Idle);
      setBadgeFileId(null);
    }
  }, [isFincenLink, contextFileId, badgeFileId]);

  // Only show badge if it belongs to the current file or no file tracking yet
  const badgeMatchesCurrentFile = !isFincenLink || !badgeFileId || contextFileId === badgeFileId;
  const effectiveBadgeContent = badgeMatchesCurrentFile ? badgeContent : NavLinkBadgeStatus.Idle;

  if (effectiveBadgeContent === NavLinkBadgeStatus.Success && !shouldShowDot) {
    return (
      <CheckCircleOutlineOutlined
        className={clsx(classes.badge, classes.checkCircle)}
        fontSize="small"
        data-testid="NavBadge-success"
      />
    );
  } else if (effectiveBadgeContent === NavLinkBadgeStatus.Warning && !shouldShowDot) {
    return (
      <ReportProblemOutlined
        className={clsx(classes.badge, classes.warningOutlined)}
        fontSize="small"
        data-testid="NavBadge-warning"
      />
    );
  } else if (effectiveBadgeContent === NavLinkBadgeStatus.Error && !shouldShowDot) {
    return (
      <ErrorOutlineOutlined
        className={clsx(classes.badge, classes.errorOutlined)}
        fontSize="small"
        data-testid="NavBadge-error"
      />
    );
  }

  const hasBadgeContentFn = () => !!badgeContentFunction && badgeContent != null && badgeContent !== 0;

  return (
    <StyledBadge
      badgeContent={shouldShowDot ? null : effectiveBadgeContent}
      variant={shouldShowDot && hasBadgeContentFn() ? 'dot' : variant}
      color={color}
      className={effectiveBadgeContent as string}
      data-testid={shouldShowDot ? 'NavBadge-dot' : 'NavBadge-generic'}
    />
  );
};

export { NavBadge };
